/**
 * TripCard — the canonical "Choose your Adventure" / Ascent card.
 *
 * Used on the home page (UpcomingTrips), on Similar Ascents inside
 * ExpeditionDetailPage, and on Similar Courses inside ClassDetailPage.
 * Encapsulates the full markup so all three locations stay visually
 * identical and pick up future updates from a single source.
 *
 * Props:
 *   trip          — same shape as objects returned by `useTrips()`
 *   linkTo        — destination route (e.g. `/expeditions/${id}`)
 *   testIdPrefix  — used to compose data-testid attributes
 *   showDeadline  — render the "Bookings until" footer line (default true)
 *   showPriceTiles — false to render a single line price (used inside the
 *                    booking sidebar where space is tight); default true
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Mountain, Users, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ASCENT_GRADE_DESCRIPTIONS, baseAscentGrade, tCategory, tEventSubcategory } from '../data/tripsTaxonomy';
import { grUpper } from '../lib/grText';
import { smartImg, smartSrcSet } from '../lib/imgUrl';
import { fmtDate } from '../lib/dateFormat';
import { earliestDepartureTime } from '../lib/pickup';

function getGradeColor(grade) {
  const base = parseInt(baseAscentGrade(grade), 10);
  if (!base) return 'bg-gray-100 text-gray-800';
  if (base <= 2) return 'bg-green-100 text-green-800';
  if (base <= 4) return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
}

function getCategoryColor(category) {
  switch (category) {
    case 'Hymettus': return 'bg-blue-100 text-blue-800';
    case 'Greece':   return 'bg-green-100 text-green-800';
    case 'Abroad':   return 'bg-purple-100 text-purple-800';
    case 'Event':    return 'bg-orange-100 text-orange-800';
    default:         return 'bg-gray-100 text-gray-800';
  }
}

export default function TripCard({
  trip,
  linkTo,
  testIdPrefix = 'trip-',
  showDeadline = true,
  hideDeparture = false,
}) {
  const { t, i18n } = useTranslation();
  const isGr = i18n.language === 'gr';

  const seatsLeft = trip.availableSeats != null ? trip.availableSeats : (trip.capacity || 0);
  const totalSeats = trip.totalSeats || trip.capacity || 0;
  const isCancelled = trip.status === 'cancelled' || trip.cancelled === true;
  const isWaitingList = !isCancelled && trip.status === 'waiting_list';
  const isFull = !isCancelled && !isWaitingList && (trip.status === 'sold_out' || seatsLeft === 0);
  const gradeBase = baseAscentGrade(trip.difficulty);
  const gradeDesc = ASCENT_GRADE_DESCRIPTIONS[gradeBase];

  return (
    <Link to={linkTo} className="block group" data-testid={`${testIdPrefix}${trip.id}`}>
      <Card
        className={`overflow-hidden border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 bg-white h-full flex flex-col ${isCancelled ? 'opacity-90' : ''}`}
      >
        {/* Image with overlay badges */}
        <div className="relative h-56 overflow-hidden bg-gradient-to-br from-slate-200 to-slate-300">
          {trip.image ? (
            <img
              loading="lazy"
              decoding="async"
              src={smartImg(trip.image, 800)}
              srcSet={smartSrcSet(trip.image, [400, 800, 1200]) || undefined}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              width={800}
              height={500}
              alt={isGr ? (trip.title_gr || trip.title) : trip.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              <Mountain className="w-16 h-16 opacity-40" />
            </div>
          )}

          {/* Category + difficulty badges + (when applicable) waiting-list pill — all inline so they share the same baseline */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-1">
            <Badge
              className={`${
                trip.kind === 'championship'
                  ? 'bg-yellow-100 text-yellow-900'
                  : getCategoryColor(trip.kind === 'event' ? 'Event' : trip.category)
              } text-xs font-bold shadow-sm`}
              data-testid={`${testIdPrefix}category-${trip.id}`}
            >
              {trip.kind === 'championship'
                ? (isGr ? 'Πρωτάθλημα' : 'Championship')
                : trip.kind === 'event'
                ? (isGr ? 'Εκδήλωση' : 'Event')
                : tCategory(trip.kind || 'expedition', trip.category, i18n.language)}
            </Badge>
            {trip.kind === 'event' && trip.category && (
              <Badge
                className="bg-amber-50 text-amber-800 border border-amber-200 text-xs font-bold shadow-sm"
                data-testid={`${testIdPrefix}event-sub-${trip.id}`}
              >
                {tEventSubcategory(trip.category, i18n.language)}
              </Badge>
            )}
            {/* Iter239 — championships surface their discipline (Rock Climbing/…)
                as a secondary tag next to the yellow "Championship" badge. */}
            {trip.kind === 'championship' && trip.category && (
              <Badge
                className="bg-white/90 text-gray-800 border border-gray-200 text-xs font-semibold shadow-sm"
                data-testid={`${testIdPrefix}champ-discipline-${trip.id}`}
              >
                {trip.category}
              </Badge>
            )}
            {trip.difficulty && trip.kind !== 'championship' && (
              <Badge
                className={`${getGradeColor(trip.difficulty)} text-xs font-bold shadow-sm`}
                title={isGr ? gradeDesc?.gr : gradeDesc?.en}
                data-testid={`${testIdPrefix}grade-${trip.id}`}
              >
                {(isGr ? 'ΒΔ ' : 'Gr ') + trip.difficulty}
              </Badge>
            )}
            {isWaitingList && (
              <Badge
                className="bg-amber-700 text-white border-amber-700 text-xs font-bold shadow-sm"
                data-testid={`${testIdPrefix}waiting-list-${trip.id}`}
              >
                {isGr ? 'ΛΙΣΤΑ ΑΝΑΜΟΝΗΣ' : 'WAITING LIST'}
              </Badge>
            )}
          </div>

          {/* Status overlay — cancelled > sold-out > few-seats hint
              (waiting-list is rendered inline with the category/difficulty
              badges above, so it never reaches this branch.) */}
          {isCancelled ? (
            <div
              className="absolute inset-0 bg-black/60 flex items-center justify-center"
              data-testid={`${testIdPrefix}cancelled-${trip.id}`}
            >
              <span className="bg-gray-800 text-white px-4 py-2 rounded-lg font-bold text-sm tracking-wider">
                {isGr ? 'ΑΚΥΡΩΘΗΚΕ' : 'CANCELLED'}
              </span>
            </div>
          ) : isFull ? (
            <div
              className="absolute inset-0 bg-black/60 flex items-center justify-center"
              data-testid={`${testIdPrefix}soldout-${trip.id}`}
            >
              <span className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold text-sm tracking-wider">
                {isGr ? 'ΕΞΑΝΤΛΗΘΗΚΕ' : 'SOLD OUT'}
              </span>
            </div>
          ) : !isWaitingList && seatsLeft > 0 && seatsLeft <= 5 ? (
            <div className="absolute bottom-3 left-3">
              <Badge className="bg-amber-700 text-white text-[10px]">
                <Users className="w-3 h-3 mr-1" />
                {isGr ? `${seatsLeft} θέσεις` : `${seatsLeft} seats left`}
              </Badge>
            </div>
          ) : null}
        </div>

        {/* Body */}
        <CardContent className="p-5 flex flex-col flex-1">
          <div className="flex items-center gap-x-2 gap-y-1 text-xs text-gray-500 mb-3 flex-wrap">
            <div className="inline-flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>{trip.date}</span>
            </div>
            {trip.timeWindow && (
              <span
                className="text-[11px] text-gray-500"
                data-testid={`${testIdPrefix}time-window-${trip.id}`}
              >
                · {trip.timeWindow}
              </span>
            )}
            {/* Iter469 — Hide the "Αναχώρηση HH:MM" pill for events,
                and whenever the caller opts out via `hideDeparture`
                (used in the Archive page). Events show location instead,
                so a departure pill next to the date is redundant. */}
            {trip.kind !== 'event' && !hideDeparture && (() => {
              const departure = earliestDepartureTime(trip);
              if (!departure) return null;
              return (
                <div
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-800 bg-blue-50 ring-1 ring-blue-100 rounded-full px-2 py-0.5"
                  data-testid={`${testIdPrefix}departure-${trip.id}`}
                >
                  <Clock className="w-3 h-3" />
                  <span>{isGr ? 'Αναχώρηση' : 'Departure'} {departure}</span>
                </div>
              );
            })()}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-[#1e3a8a] transition-colors line-clamp-2">
            {isGr ? (trip.title_gr || trip.title) : trip.title}
          </h3>

          {/* Seats availability */}
          <div className="flex items-center gap-1.5 text-xs mb-1" data-testid={`${testIdPrefix}seats-${trip.id}`}>
            <Users className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-gray-600">{isGr ? 'Θέσεις:' : 'Seats:'}</span>
            <span
              className={
                isCancelled
                  ? 'text-gray-500 font-medium line-through'
                  : seatsLeft > 5
                    ? 'text-green-600 font-medium'
                    : seatsLeft > 0
                      ? 'text-orange-600 font-medium'
                      : 'text-red-600 font-medium'
              }
            >
              {seatsLeft}/{totalSeats}
            </span>
            {!isCancelled && isFull && (
              <span className="text-red-600 font-semibold ml-1">{isGr ? '· Πλήρες' : '· Full'}</span>
            )}
          </div>

          {/* Booking deadline */}
          {showDeadline && trip.booking_deadline && !isCancelled ? (
            <div
              className="text-[11px] text-gray-500 mb-3"
              data-testid={`${testIdPrefix}deadline-${trip.id}`}
            >
              {isGr ? 'Κρατήσεις έως: ' : 'Bookings until: '}
              <span className="font-medium text-gray-700">{fmtDate(trip.booking_deadline)}</span>
            </div>
          ) : (
            <div className="mb-2" />
          )}

          {/* Member / Guest price tiles */}
          <div className={`grid grid-cols-2 gap-2 mt-auto ${isCancelled ? 'opacity-60' : ''}`}>
            {trip.pricing_pending ? (
              <div
                className="col-span-2 flex items-center justify-center bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5 text-xs font-semibold text-gray-700"
                data-testid={`${testIdPrefix}price-tba-${trip.id}`}
              >
                {isGr ? 'Τιμή: Θα ανακοινωθεί' : 'Price: to be announced'}
              </div>
            ) : trip.isFreeForAll ? (
              <div
                className="col-span-2 flex items-center justify-center bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5 text-xs font-semibold text-emerald-800"
                data-testid={`${testIdPrefix}free-all-${trip.id}`}
              >
                {isGr ? 'Δωρεάν για όλους' : 'Free for all'}
              </div>
            ) : (
              <>
                <div
                  className="flex flex-col bg-green-50 border border-green-200 rounded-md px-2 py-1"
                  data-testid={`${testIdPrefix}member-price-${trip.id}`}
                >
                  <span className="text-[11px] uppercase tracking-wider text-green-700 font-medium">
                    {isGr ? grUpper('Μέλος') : 'Member'}
                  </span>
                  <span className="text-sm font-bold text-green-900 leading-tight">
                    {trip.isFreeForMembers ? (isGr ? 'Δωρεάν' : 'Free') : trip.memberPrice}
                  </span>
                </div>
                <div
                  className="flex flex-col bg-gray-50 border border-gray-200 rounded-md px-2 py-1"
                  data-testid={`${testIdPrefix}guest-price-${trip.id}`}
                >
                  <span className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">
                    {isGr ? grUpper('Μη μέλος') : 'Guest'}
                  </span>
                  <span className="text-sm font-bold text-gray-900 leading-tight">
                    {trip.guestPriceLabel || trip.price}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Cancellation note */}
          <p
            className="mt-3 text-[11px] text-gray-500 leading-snug"
            data-testid={`${testIdPrefix}cancel-note-${trip.id}`}
          >
            {isCancelled
              ? (isGr
                  ? 'Η εκδήλωση έχει ακυρωθεί από τον σύλλογο.'
                  : 'This trip has been cancelled by the club.')
              : (isGr
                  ? 'Η προκαταβολή δεν επιστρέφεται σε περίπτωση ακύρωσης!'
                  : 'Deposit is non-refundable in case of cancellation!')}
          </p>
        </CardContent>
      </Card>
    </Link>
  );
}
